import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import { nextWeek, prevWeek, thisWeek, weekDays } from '../dispatch/weekModel.js';
import { useJobAccess } from '../jobs/useJobAccess.js';
import { bucketByDay, fetchWindowFor } from './scheduleBoard.js';
import { type ScheduleRow, useImportJobMutation, useScheduleQuery } from './useScheduleQuery.js';

/**
 * The ServiceTitan page — what work is booked, read straight from ServiceTitan.
 *
 * WHY THIS EXISTS: the service side of the business runs entirely in ServiceTitan, so none of it was
 * visible in QELM and the office had no single place to see what is coming in.
 *
 * SHAPED LIKE `/dispatch`, ON PURPOSE. Seven Monday-start day columns above `lg`, a single-day
 * agenda below it, and `?week=` in the URL — the same pattern, the same week maths (imported from
 * `dispatch/weekModel`, not reimplemented, so "Monday-start week" cannot drift into two
 * definitions), and the same responsive split for the same documented reason: a seven-column board
 * is not usable on a phone.
 *
 * ⚠ READ-ONLY, AND IT MUST LOOK IT. `/dispatch` cards drag; these cannot — we never write to
 * ServiceTitan. So there is no drag affordance, no drop target and no cursor-grab styling. A board
 * that looks draggable and is not would be worse than one that plainly is not.
 *
 * ⚠ EVERY BUSINESS UNIT IS SHOWN, and the unit is on every card. This tenant runs HVAC, Plumbing,
 * Electrical and Renovation units through one ServiceTitan account, and their names are not
 * consistent (`Plumbing ` has a trailing space, `[P]Plumbing - Install` a prefix). Filtering is by
 * unit ID and never by name, and nothing is hidden by default — hiding work by guessing at a name is
 * how a board quietly loses a job.
 */

const CARD = 'rounded-md border border-border bg-card p-2 text-left';
const COLUMN = 'rounded-md border border-border bg-muted/40 p-2 align-top';
const CHIP = 'rounded-full border border-border px-2 py-0.5 text-xs';

const routeApi = getRouteApi('/servicetitan');

function timeOf(iso: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function dayHeading(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', timeZone: 'UTC' });
}

function weekLabel(days: string[]): string {
  const first = new Date(`${days[0]}T00:00:00Z`);
  return first.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * One appointment, as it appears in a day column and in the agenda.
 *
 * The import affordance is the point of the whole page: press it and there is a QELM job to hang
 * expenses on. Already-imported rows show a link to that job INSTEAD of the button, so the same call
 * cannot be imported twice by accident — and the server refuses anyway, backed by a unique index.
 *
 * `onImport` is omitted for a viewer the import endpoint would refuse (Manager+ and jobs.create),
 * and then neither import button is drawn.
 */
function AppointmentCard({
  row,
  onImport,
  importing,
}: {
  row: ScheduleRow;
  onImport?: (serviceTitanJobId: number) => void;
  importing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <article className={CARD}>
      <p className="font-medium text-foreground text-sm">{timeOf(row.start)}</p>
      {row.customerName ? (
        <p className="truncate text-foreground text-sm">{row.customerName}</p>
      ) : null}
      {row.siteAddress ? (
        <p className="truncate text-muted-foreground text-xs">{row.siteAddress}</p>
      ) : null}
      {row.technicians.length > 0 ? (
        <p className="truncate text-muted-foreground text-xs">{row.technicians.join(', ')}</p>
      ) : (
        <p className="text-muted-foreground text-xs">{t('servicetitan:unassigned')}</p>
      )}
      <div className="mt-1 flex flex-wrap gap-1">
        {row.businessUnitName ? <span className={CHIP}>{row.businessUnitName.trim()}</span> : null}
        {row.jobStatus ? <span className={CHIP}>{row.jobStatus}</span> : null}
      </div>
      <div className="mt-2">
        {row.importedJobId ? (
          // Imported rows still need a way to re-read the money: a job imported before the customer
          // paid would otherwise sit at zero revenue forever. Pressing this re-runs the same import,
          // which upserts the income row rather than stacking a second one.
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/jobs/$id"
              params={{ id: String(row.importedJobId) }}
              className="text-foreground text-xs underline"
            >
              {t('servicetitan:viewJob')}
            </Link>
            {onImport ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={importing}
                onClick={() => onImport(row.jobId)}
              >
                {importing ? t('servicetitan:syncing') : t('servicetitan:syncMoney')}
              </Button>
            ) : null}
          </div>
        ) : onImport ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={importing}
            onClick={() => onImport(row.jobId)}
          >
            {importing ? t('servicetitan:importing') : t('servicetitan:createJob')}
          </Button>
        ) : null}
      </div>
    </article>
  );
}

export function ServiceTitanRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = routeApi.useSearch();
  const [unitFilter, setUnitFilter] = useState<number | 'all'>('all');
  const importJob = useImportJobMutation();
  const { canCreateFromRecord } = useJobAccess();
  const onImport = canCreateFromRecord ? (id: number) => importJob.mutate(id) : undefined;

  const weekStart = search.week ?? thisWeek();
  const days = useMemo(() => weekDays(weekStart), [weekStart]);
  const window = useMemo(() => fetchWindowFor(days), [days]);

  const query = useScheduleQuery(window.from, window.to);
  const rows = query.data?.rows ?? [];

  const units = useMemo(() => {
    const seen = new Map<number, string>();
    for (const r of rows) {
      if (r.businessUnitId != null) {
        seen.set(r.businessUnitId, (r.businessUnitName ?? '').trim() || `#${r.businessUnitId}`);
      }
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const visible = useMemo(
    () => (unitFilter === 'all' ? rows : rows.filter((r) => r.businessUnitId === unitFilter)),
    [rows, unitFilter],
  );

  const { columns, undated } = useMemo(() => bucketByDay(visible, days), [visible, days]);

  // The agenda below `lg` shows one day. Defaults to today when today is in view.
  const today = thisWeek() === weekStart ? new Date().toISOString().slice(0, 10) : null;
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const agendaDay = selectedDay && days.includes(selectedDay) ? selectedDay : (today ?? days[0]);

  function goto(week: string) {
    void navigate({ to: '/servicetitan', search: week === thisWeek() ? {} : { week } });
  }

  if (query.isPending) {
    return (
      <div className="flex justify-center p-12">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError) {
    const code = query.error instanceof ApiError ? query.error.message : '';
    const key =
      {
        servicetitan_not_configured: 'errorNotConfigured',
        servicetitan_auth_rejected: 'errorAuthRejected',
        servicetitan_api_forbidden: 'errorApiForbidden',
        servicetitan_credentials_unreadable: 'errorUnreadable',
        servicetitan_encryption_unavailable: 'errorEncryption',
      }[code] ?? 'errorUnavailable';

    return (
      <div className="space-y-4">
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('servicetitan:title')}
        </h1>
        <div className="rounded-md border border-border bg-card p-4">
          <p className="text-destructive text-sm">{t(`servicetitan:${key}`)}</p>
          <Button
            type="button"
            variant="secondary"
            className="mt-3"
            onClick={() => query.refetch()}
          >
            {t('servicetitan:retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-semibold text-2xl text-foreground">
            {t('servicetitan:title')}
          </h1>
          <p className="text-muted-foreground text-sm">{t('servicetitan:subtitle')}</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          disabled={query.isFetching}
          onClick={() => query.refetch()}
        >
          <RefreshCw className="size-4" aria-hidden="true" />
          {query.isFetching ? t('servicetitan:refreshing') : t('servicetitan:refresh')}
        </Button>
      </div>

      {/* Week navigation — same semantics as /dispatch, so the two boards move alike. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label={t('servicetitan:prevWeek')}
          onClick={() => goto(prevWeek(weekStart))}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
        </Button>
        <span className="font-medium text-foreground text-sm">
          {t('servicetitan:weekOf', { date: weekLabel(days) })}
        </span>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label={t('servicetitan:nextWeek')}
          onClick={() => goto(nextWeek(weekStart))}
        >
          <ChevronRight className="size-4" aria-hidden="true" />
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => goto(thisWeek())}>
          {t('servicetitan:thisWeek')}
        </Button>
      </div>

      {units.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-xs">{t('servicetitan:filterUnit')}</span>
          <Button
            type="button"
            variant={unitFilter === 'all' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setUnitFilter('all')}
          >
            {t('servicetitan:allUnits')}
          </Button>
          {units.map(([id, name]) => (
            <Button
              key={id}
              type="button"
              variant={unitFilter === id ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setUnitFilter(id)}
            >
              {name}
            </Button>
          ))}
        </div>
      ) : null}

      {importJob.isError ? (
        <p className="rounded-md border border-border bg-card p-3 text-destructive text-sm">
          {t('servicetitan:importFailed')}
        </p>
      ) : null}

      {query.data?.truncated ? (
        <p className="rounded-md border border-border bg-muted p-3 text-foreground text-sm">
          {t('servicetitan:truncated')}
        </p>
      ) : null}

      {undated.length > 0 ? (
        // Rows with an unreadable start have no column. Surfaced rather than dropped silently.
        <p className="rounded-md border border-border bg-muted p-3 text-foreground text-sm">
          {t('servicetitan:undated', { count: undated.length })}
        </p>
      ) : null}

      {/* ── The week board: lg and up ─────────────────────────────────────────
          A 7-column board is not usable on a phone (the same finding that gave /dispatch its day
          view), so this is hidden below lg and the agenda beneath takes over. */}
      <div className="hidden lg:grid lg:grid-cols-7 lg:gap-2">
        {days.map((day) => {
          const dayRows = columns.get(day) ?? [];
          const isToday = day === new Date().toISOString().slice(0, 10);
          return (
            <div key={day} className={COLUMN}>
              <h2
                className={`mb-2 font-display font-semibold text-sm ${
                  isToday ? 'text-foreground' : 'text-muted-foreground'
                }`}
              >
                {dayHeading(day)}
              </h2>
              <div className="space-y-2">
                {dayRows.length === 0 ? (
                  <p className="text-muted-foreground text-xs">—</p>
                ) : (
                  dayRows.map((row) => (
                    <AppointmentCard
                      key={row.appointmentId}
                      row={row}
                      onImport={onImport}
                      importing={importJob.isPending && importJob.variables === row.jobId}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── The agenda: below lg ─────────────────────────────────────────────── */}
      <div className="lg:hidden">
        <div className="mb-2 flex flex-wrap gap-1">
          {days.map((day) => (
            <Button
              key={day}
              type="button"
              size="sm"
              variant={day === agendaDay ? 'primary' : 'secondary'}
              onClick={() => setSelectedDay(day)}
            >
              {dayHeading(day)}
            </Button>
          ))}
        </div>
        <div className="space-y-2">
          {(columns.get(agendaDay) ?? []).length === 0 ? (
            <div className="rounded-md border border-border bg-card p-4">
              <p className="text-muted-foreground text-sm">{t('servicetitan:empty')}</p>
            </div>
          ) : (
            (columns.get(agendaDay) ?? []).map((row) => (
              <AppointmentCard
                key={row.appointmentId}
                row={row}
                onImport={onImport}
                importing={importJob.isPending && importJob.variables === row.jobId}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
